import type { Helpers } from "./endpoints-shared";
import type { In, Out } from "./operations";

/**
 * The front desk module: desks and their layouts, the staff at each desk, each user's own choices, and the arrivals and status feeds.
 * Every method names its operation id, so a renamed route fails to compile after `npm run api:generate`.
 */
export function frontdeskEndpoints({ get, post, patch, put, del }: Helpers) {
  return {
    /** The desks the user works at; managers get every desk. */
    listDesks: (signal?: AbortSignal) => get<Out<"frontdesk_list_desks">>("/frontdesk/desks", { signal }),
    createDesk: (input: In<"frontdesk_create_desk">) => post<Out<"frontdesk_create_desk">>("/frontdesk/desks", input),
    /** A desk with its layout as the signed-in user sees it (their hidden widgets and sizes applied). */
    getDesk: (id: string, signal?: AbortSignal) => get<Out<"frontdesk_get_desk">>(`/frontdesk/desks/${id}`, { signal }),
    updateDesk: (id: string, input: In<"frontdesk_update_desk">) => patch<Out<"frontdesk_update_desk">>(`/frontdesk/desks/${id}`, input),
    deleteDesk: (id: string) => del<Out<"frontdesk_delete_desk">>(`/frontdesk/desks/${id}`),
    putLayout: (id: string, input: In<"frontdesk_put_layout">) => put<Out<"frontdesk_put_layout">>(`/frontdesk/desks/${id}/layout`, input),

    /** The club's users, to choose who works at a desk. */
    listStaff: (signal?: AbortSignal) => get<Out<"frontdesk_list_staff">>("/frontdesk/staff", { signal }),
    listDeskStaff: (id: string, signal?: AbortSignal) => get<Out<"frontdesk_list_desk_staff">>(`/frontdesk/desks/${id}/staff`, { signal }),
    assignStaff: (id: string, userId: string) => put<Out<"frontdesk_assign_staff">>(`/frontdesk/desks/${id}/staff/${userId}`, {}),
    unassignStaff: (id: string, userId: string) => del<Out<"frontdesk_unassign_staff">>(`/frontdesk/desks/${id}/staff/${userId}`),

    /** Hide and resize widgets for the signed-in user on one desk. */
    putPrefs: (id: string, input: In<"frontdesk_put_prefs">) => put<Out<"frontdesk_put_prefs">>(`/frontdesk/desks/${id}/prefs`, input),
    resetPrefs: (id: string) => del<Out<"frontdesk_reset_prefs">>(`/frontdesk/desks/${id}/prefs`),

    /** Who is expected on a club-local day (YYYY-MM-DD); today when left out. */
    arrivals: (date?: string, signal?: AbortSignal) => get<Out<"frontdesk_get_arrivals">>("/frontdesk/arrivals", { query: { date }, signal }),
    status: (signal?: AbortSignal) => get<Out<"frontdesk_get_status">>("/frontdesk/status", { signal }),
  };
}
